import type { PlayerSliceDeps } from "../deps";
import type {
  PlayerStore,
  RecommendationsState,
  RecommendationsActions,
  NamedSlice,
} from "../types";
import type { QueueItem } from "../../utils/queue.types";
import type { CatalogSong } from "../../domain/types";
import { selectActiveQueue, selectActiveSongId } from "../selectors";
import { backOff } from "exponential-backoff";
import { isExpectedTRPCError } from "@utils/queryClient";
import { isPlayableSong } from "../../utils/queue.utils";

const BACKOFF_OPTIONS = (message: string) => {
  return {
    numOfAttempts: 3,
    startingDelay: 500,
    timeMultiple: 2,
    retry(e: Error, attemptNumber: number) {
      console.audio.warn(
        `Error: ${message} for attempt ${attemptNumber}: ${e.message}`,
      );
      return attemptNumber < 3;
    },
  };
};

export const createRecommendationsSlice = (
  deps: PlayerSliceDeps,
): NamedSlice<
  RecommendationsState & { actions: RecommendationsActions },
  PlayerStore
> => {
  const { fetchForYouRecommendedSongs, fetchSimilarSongsForSongIds } =
    deps.catalog;
  return (set, get) => ({
    isLoadingMore: false,
    lastError: null,
    lastFetchedSongId: null,
    lastViewedSongId: null,
    paginationUserId: null,

    playlistIsLoadingMore: false,
    playlistLastError: null,
    playlistRecsCursorSongId: null,

    actions: {
      loadForYouInitial: async (options) => {
        const forceReplace = options?.forceReplace ?? false;
        const resetCursors = options?.resetCursors ?? false;
        const userId = options?.userId;
        const suppressGeneration = options?.suppressGeneration ?? false;
        const importOnly = options?.importOnly ?? false;

        const { lastViewedSongId, lastFetchedSongId } = get();
        const effectiveLastViewedSongId = resetCursors
          ? undefined
          : (lastViewedSongId ?? undefined);
        const effectiveLastFetchedSongId = resetCursors
          ? undefined
          : (lastFetchedSongId ?? undefined);

        return backOff(
          async () => {
            const songs = await fetchForYouRecommendedSongs({
              limit: 10,
              lastViewedSongId: effectiveLastViewedSongId,
              lastFetchedSongId: effectiveLastFetchedSongId,
              // Skip cache when resetting cursors (auth change) to avoid stale data
              skipCache: resetCursors,
              suppressGeneration,
              importOnly,
            });
            // External songs have no playable audio — never let them into the
            // queue (mirrors the same filter in queue.slice.ts's
            // playCollection/syncPlaylistQueue/addNext).
            const items = songs
              .filter(isPlayableSong)
              .map((song: CatalogSong) => ({
                qid: `${song.id}-${Math.random().toString(36).substr(2, 9)}`,
                metadata: song,
              }));
            set(
              (state) => {
                // When refreshing due to auth change, reset cursors so pagination
                // doesn't apply across different recommendation sources.
                if (resetCursors) {
                  state.lastFetchedSongId = null;
                  // Keep lastViewedSongId if something is actively playing; otherwise reset.
                  if (!selectActiveSongId(state)) {
                    state.lastViewedSongId = null;
                  }
                }

                const shouldInitOrReplace =
                  forceReplace ||
                  !state.foryouQueue ||
                  state.foryouQueue.items.length === 0;

                if (shouldInitOrReplace) {
                  state.foryouQueue = {
                    type: "foryou",
                    items: items,
                    currentIndex: items.length > 0 ? 0 : -1,
                  };
                  // Only set last viewed if nothing is playing
                  if (items.length > 0 && !selectActiveSongId(state)) {
                    state.lastViewedSongId = items[0].metadata.id;
                  }
                }

                // Advance the cursor past every song the server returned,
                // including ones filtered out as unplayable — otherwise a
                // trailing external song would make the next page re-request
                // (and re-filter) the same song forever.
                if (songs.length > 0) {
                  state.lastFetchedSongId = songs.at(-1)?.id ?? null;
                }

                // Update paginationUserId if provided (tracks which user owns these cursors)
                if (userId !== undefined) {
                  state.paginationUserId = userId;
                }
              },
              false,
              "recommendations/loadForYouInitial",
            );

            // If initial load returned fewer songs than requested, immediately
            // fetch more so the queue isn't too short for swiping
            if (items.length > 0 && items.length < 5) {
              get().actions.fetchMoreRecommendations().catch(console.error);
            }

            return { count: items.length };
          },
          { ...BACKOFF_OPTIONS("bootstrap for you") },
        ).catch((error) => {
          if (!isExpectedTRPCError(error)) {
            console.error(error);
          }
          return { count: 0 };
        });
      },

      fetchMoreRecommendations: async () => {
        const queue = selectActiveQueue(get());
        if (!queue || queue.type !== "foryou" || get().isLoadingMore) return;

        const { lastViewedSongId, lastFetchedSongId } = get();

        set(
          (state) => {
            state.isLoadingMore = true;
          },
          false,
          "recommendations/fetchMoreRecommendations/start",
        );

        try {
          await backOff(
            async () => {
              // Use the stored raw cursor (advanced past every song the
              // server returned, including ones filtered out as unplayable —
              // see the `set` below and the matching comment in
              // loadForYouInitial), NOT the tail of the current *filtered*
              // queue: if the last song the server returned was unplayable,
              // the filtered queue's tail would be an earlier song, and
              // requesting from there would re-fetch (and re-filter) the
              // same trailing song forever.
              const songs = await fetchForYouRecommendedSongs({
                limit: 10,
                lastFetchedSongId: lastFetchedSongId ?? undefined,
                lastViewedSongId: lastViewedSongId ?? undefined,
              });

              // External songs have no playable audio — never let them into
              // the queue.
              const newItems = songs
                .filter(isPlayableSong)
                .map((song: CatalogSong) => ({
                  qid: `${song.id}-${Math.random().toString(36).substr(2, 9)}`,
                  metadata: song,
                }));

              set(
                (state) => {
                  if (state.foryouQueue) {
                    state.foryouQueue.items.push(...newItems);
                    // Advance past every song the server returned (see the
                    // matching comment in loadForYouInitial) so a trailing
                    // unplayable song can't stall pagination.
                    if (songs.length > 0) {
                      state.lastFetchedSongId = songs.at(-1)?.id ?? null;
                    }
                    if (
                      state.foryouQueue.currentIndex === -1 &&
                      state.foryouQueue.items.length > 0
                    ) {
                      state.foryouQueue.currentIndex = 0;
                      state.lastViewedSongId =
                        state.foryouQueue.items[0].metadata.id;
                    }
                  }
                  state.isLoadingMore = false;
                  state.lastError = null;
                },
                false,
                "recommendations/fetchMoreRecommendations/success",
              );
            },
            {
              ...BACKOFF_OPTIONS("fetch more recommendations"),
            },
          );
        } catch (e) {
          set(
            (state) => {
              state.isLoadingMore = false;
              state.lastError = (e as Error).message;
            },
            false,
            "recommendations/fetchMoreRecommendations/error",
          );
        }
      },

      fetchMorePlaylistRecommendations: async (options) => {
        const { limit = 10 } = options || {};
        const state = get();
        const queue = state.playlistQueue;

        if (!queue || state.playlistIsLoadingMore) return;

        const collection = queue.collection;
        const cursorSongId = state.playlistRecsCursorSongId ?? undefined;

        set(
          (state) => {
            state.playlistIsLoadingMore = true;
          },
          false,
          "recommendations/fetchMorePlaylistRecommendations/start",
        );

        try {
          await backOff(
            async () => {
              // Seed with a window around current index
              const currentIndex = queue.currentIndex;
              const windowSize = 15;
              const start = Math.max(0, currentIndex - windowSize);
              const end = Math.min(
                queue.items.length,
                currentIndex + windowSize + 1,
              );
              const seedItems = queue.items.slice(start, end);
              const seedSongIds = Array.from(
                new Set(seedItems.map((i) => i.metadata.id)),
              );

              const songs = await fetchSimilarSongsForSongIds({
                songIds: seedSongIds,
                limit,
                cursorSongId,
              });

              const existingSongIds = new Set(
                queue.items.map((i) => i.metadata.id),
              );
              // Unpublished/external songs have no playable audio — never let
              // them into the queue. Cursor advancement below still uses the
              // unfiltered `songs` so pagination doesn't re-request this page.
              const newSongs = songs.filter(
                (s: CatalogSong) =>
                  !existingSongIds.has(s.id) && isPlayableSong(s),
              );

              const newItems: QueueItem[] = newSongs.map(
                (song: CatalogSong) => ({
                  qid: `${song.id}-${Math.random().toString(36).substr(2, 9)}`,
                  metadata: song,
                  source: "playlist-recommendation",
                }),
              );

              set(
                (state) => {
                  const currentQueue = state.playlistQueue;
                  const isSameCollection =
                    currentQueue?.collection.id === collection.id &&
                    currentQueue.collection.type === collection.type;

                  if (isSameCollection && currentQueue) {
                    // ONE flat queue (continuous-listening model, 2026-08-02):
                    // recommendations append directly to queue.items — visible,
                    // tappable, reorderable like any row. Shuffle/repeat scope
                    // them out until reached via playlistScopeEnd, so no side
                    // buffer is needed. Appends also land on the shuffle
                    // snapshot so turning shuffle off keeps them.
                    currentQueue.items.push(...newItems);
                    if (currentQueue.shuffleState?.originalOrder) {
                      currentQueue.shuffleState.originalOrder.push(...newItems);
                    }
                  }
                  state.playlistIsLoadingMore = false;
                  state.playlistLastError = null;
                  // Advance keyset cursor to last returned song (before client dedup)
                  // so next call pages past everything we already got
                  if (isSameCollection && songs.length > 0) {
                    state.playlistRecsCursorSongId = songs[songs.length - 1].id;
                  }
                },
                false,
                "recommendations/fetchMorePlaylistRecommendations/success",
              );
            },
            { ...BACKOFF_OPTIONS("fetch more playlist recommendations") },
          );
        } catch (e) {
          set(
            (state) => {
              state.playlistIsLoadingMore = false;
              state.playlistLastError = (e as Error).message;
            },
            false,
            "recommendations/fetchMorePlaylistRecommendations/error",
          );
        }
      },

      setLastViewedSongId: (songId) => {
        set(
          (state) => {
            state.lastViewedSongId = songId;
          },
          false,
          "recommendations/setLastViewedSongId",
        );
      },
    },
  });
};
